import React, { ReactNode, useState } from 'react'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import type { Tenant } from '../api/types'
import type { AdminTenantResp } from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

type TenantRow = Tenant & { protected?: boolean }

const columns: CrudColumn<TenantRow>[] = [
  { key: 'id', header: 'ID' },
  { key: 'status', header: 'Status' },
  { key: 'issuer', header: 'Issuer' },
  {
    key: 'require_mtls',
    header: 'mTLS',
    render: ({ require_mtls: r }) => (r ? 'Required' : '—'),
  },
  {
    key: 'created_at',
    header: 'Created',
    render: ({ created_at: createdAt }) => createdAt ?? '—',
  },
]

const createFields: CrudField[] = [
  { name: 'id', label: 'ID', type: 'text', required: true },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'suspended', label: 'Suspended' },
    ],
  },
  { name: 'requireMtls', label: 'Require mTLS', type: 'checkbox' },
]

/**
 * Map the generated AdminTenantResp (camelCase, as returned by the real
 * client — see `adminListTenants`) → TenantRow (snake_case, matching the
 * app-wide `Tenant` model in api/types.ts). The previous version of this
 * page read the raw response through an `as unknown as TenantRow[]` cast,
 * which silently relabeled `requireMtls`/`createdAt` as `require_mtls`/
 * `created_at` — those keys never existed on the real object, so the mTLS
 * and Created columns always rendered blank ('—') regardless of the actual
 * server value. This explicit field-by-field mapper fixes that latent bug
 * (OIDCPAM Gate-6 Phase 4c).
 */
function toTenantRow(t: AdminTenantResp): TenantRow {
  return {
    id: t.id,
    name: t.name,
    status: t.status,
    issuer: t.issuer,
    require_mtls: t.requireMtls,
    created_at: t.createdAt,
    updated_at: t.updatedAt,
    protected: t.protected,
  }
}

export const Tenants = (): JSX.Element => {
  // Task 17a: system-admin tenant CRUD served by the genapi-generated client.
  const client = useAdminGenClient()
  // Keep the AppBar TenantSwitcher's tenant list in sync after CRUD so adding
  // or removing a tenant updates the switcher live (no reload required).
  const { refreshTenants } = useActiveTenant()

  const [pendingDelete, setPendingDelete] = useState<TenantRow | null>(null)

  const rs = useResource<TenantRow>({
    list: () =>
      client
        .adminListTenants({})
        .then((resp) =>
          // Soft-deleted tenants are hidden from the active management view:
          // adminDeleteTenant is a soft delete (status → "deleted") that the
          // server keeps returning for audit, but a deleted tenant is dead —
          // it is rejected by checkPathTenant and must not clutter the list or
          // present a (no-op, idempotent) Delete button. They persist
          // server-side; only the active/suspended tenants are shown here.
          // (FE-side filter is sufficient at current scale — a single list
          // page; a status filter on the API would be needed for large-scale
          // pagination.)
          (resp.tenants ?? [])
            .map(toTenantRow)
            .filter((t) => t.status !== 'deleted')
        ),
    create: (v) => {
      const fv = v as Partial<TenantRow> & { requireMtls?: boolean }
      return client
        .adminCreateTenant({
          id: fv.id ?? '',
          name: fv.name ?? (fv.id ?? ''),
          status: fv.status ?? '',
          requireMtls: Boolean(fv.requireMtls),
        })
        .then(async (resp) => {
          await refreshTenants()
          return toTenantRow(resp)
        })
    },
    update: (id, v) =>
      client
        .adminUpdateTenant({
          tenantId: id,
          name: v.name,
          status: v.status,
        })
        .then((resp) => toTenantRow(resp)),
    remove: (id) =>
      client
        .adminDeleteTenant({ tenantId: id })
        .then(async () => {
          await refreshTenants()
        }),
  })

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (row: TenantRow): ReactNode => {
    const isSuspended = row.status === 'suspended'
    return (
      <CrudActionsRow>
        <CrudSecondaryButton
          type="button"
          onClick={() => {
            rs.update(row.id, { status: isSuspended ? 'active' : 'suspended' })
          }}
        >
          {isSuspended ? 'Activate' : 'Suspend'}
        </CrudSecondaryButton>
        {!row.protected && (
          <CrudDangerButton
            type="button"
            onClick={() => setPendingDelete(row)}
          >
            Delete
          </CrudDangerButton>
        )}
      </CrudActionsRow>
    )
  }

  return (
    <PageStack>
      <PageTitle>Tenants</PageTitle>
      {rs.error && <PamError message={rs.error} />}
      <SectionBlock>
        <SectionTitle>All Tenants</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Tenants list"
          emptyMessage="No tenants configured yet."
        />
      </SectionBlock>
      <SectionBlock>
        <SectionTitle>Create Tenant</SectionTitle>
        <CrudForm
          fields={createFields}
          onSubmit={(values) => rs.create(values as Partial<TenantRow>)}
          submitLabel="Create Tenant"
        />
      </SectionBlock>
      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete tenant"
        body={`Delete tenant "${pendingDelete?.name ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
